import { Header4 } from 'library/styles/text';
import { Icon } from 'library/components/item/styles';
import { ButtonBack } from 'library/styles/button';
import { IconBase } from 'library/styles/icon';
import { useParams, useHistory } from 'react-router-dom';
import { useEffect, useState } from 'react';
import {
  getSet, deleteSet, editSet, resetSet, batchAddToSet,
} from 'store/set/actions';
import { getSet as getSetData, getSetLoadingState } from 'store/set/selectors';
import { useDispatch, useSelector } from 'react-redux';
import DeleteItemsModal from 'library/components/modal/batch-confirm';
import EditSetModal from 'library/components/modal/color';
import NoContent from 'library/components/no-content';
import * as yup from 'yup';
import AddToContainerEntityModal from 'library/components/modal/pick-entity';
import { batchAddToProject } from 'store/project/actions';
import { FILTER_KEYS, TYPE } from 'store/browse-items/fixtures';
import { ACTIONS } from './constants';
import {
  Container, Header, Actions, Meta, ActionIcon,
} from './styles';
import PickItemsFromCatalogue from '../../../../library/components/modal/pick-items-from-catelogue';
import AddButton from '../../../../library/components/add-button';
import useProjectList from '../../../../library/hooks/use-deferred-data/use-project-list';
import { getViewMode } from '../../../../store/shared/selectors';
import { getBrowseItemsFilters as getBrowseItemsFiltersSelector } from '../../../../store/browse-items/selectors';
import { setBrowseItemsFilters, getBrowseItems } from '../../../../store/browse-items/actions';
import AddItemModal from '../../../../library/components/modal/add-item-manually';

const validation = yup.object().shape({
  name: yup
    .string()
    .required('Set name is required field')
    .min(2, 'Name is too short')
    .max(30, 'Name is too long'),
  color: yup.string(),
});

const projectIdValidation = yup.object().shape({
  id: yup.number(),
});

const Set = ({ children, openedItemId }) => {
  const dispatch = useDispatch();
  const history = useHistory();
  const { id } = useParams();
  const isLoading = useSelector(getSetLoadingState);
  const data = useSelector(getSetData);
  const [deleteModalOpened, toggleDeleteModal] = useState(false);
  const [editModalOpened, toggleEditModal] = useState(false);
  const [addToProjectModalOpened, toggleAddToProjectModal] = useState(false);
  const { options: projectOptions, isLoading: isProjectListLoading, onQueryChange: onProjectQueryChange } = useProjectList(!addToProjectModalOpened);
  const [isCatalogueOpened, toggleCatalogue] = useState(false);
  const mode = useSelector(getViewMode);
  const filters = useSelector(getBrowseItemsFiltersSelector);
  const [isAddItemModalOpened, toggleAddItemModalOpened] = useState(false);

  useEffect(() => {
    dispatch(getSet({ id }));

    return () => {
      dispatch(resetSet());

      if(history.location.pathname.includes(`/browse${mode}`)) {
        dispatch(setBrowseItemsFilters({
          ...filters,
          [FILTER_KEYS.TYPE]: TYPE.SET,
        }));
        dispatch(getBrowseItems());
      }
    };
  }, []);

  const handleDeleteSubmit = () => {
    dispatch(deleteSet({ id }));

    history.goBack();
  };

  const handleDeleteSetModalClose = () => {
    toggleDeleteModal(false);
  };

  const handleDeleteSetModalOpen = () => {
    toggleDeleteModal(true);
  };

  const handleEditSetModalClose = () => {
    toggleEditModal(false);
  };

  const handleEditSetModalOpen = () => {
    toggleEditModal(true);
  };

  const handleEditSubmit = (editedData) => {
    dispatch(editSet({ id, data: editedData }));

    handleEditSetModalClose();
  };

  const handleAddToProjectModalClose = () => {
    toggleAddToProjectModal(false);
  };

  const handleAddToProjectModalOpen = () => {
    toggleAddToProjectModal(true);
  };

  const handleAddToProjectSubmit = ({ id: projectId }) => {
    dispatch(batchAddToProject({
      ids: [data.id],
      id: projectId,
      type: 'SET',
      messages: { success: 'Set added to project' },
      resetProjects: true,
    }));

    handleAddToProjectModalClose();
  };

  const renderAction = ({ name }) => {
    switch (name) {
    case 'pencil': {
      return (
        <ActionIcon key={name} actionType={name} onClick={handleEditSetModalOpen}>
          <Icon className={`icon-${name}`} />
        </ActionIcon>
      );
    }
    case 'trash': {
      return (
        <ActionIcon key={name} actionType={name} onClick={handleDeleteSetModalOpen}>
          <Icon className={`icon-${name}`} />
        </ActionIcon>
      );
    }
    case 'projects': {
      return (
        <ActionIcon key={name} actionType={name} onClick={handleAddToProjectModalOpen}>
          <Icon className={`icon-${name}`} />
        </ActionIcon>
      );
    }
    }

    return null;
  };

  const handleCatalogueModalClose = () => {
    toggleCatalogue(false);
  };

  const handleCatalogueSubmit = (ids) => {
    dispatch(batchAddToSet({
      id,
      ids,
      messages: { success: 'Items added to set' },
      resetSet: true,
    }));

    handleCatalogueModalClose();
  };

  const actions = [{
    id: 'catalogue',
    label: 'Browse existing items',
    icon: 'icon-magnifier',
    action: () => {
      toggleCatalogue(true);
    },
  },
  {
    id: 'manually',
    label: 'Create new item',
    icon: 'icon-plus',
    action: () => {
      toggleAddItemModalOpened(true);
    },
  },
  ];

  return (
    <Container>
      <Header isShrunk={!!openedItemId}>
        <Meta>
          <ButtonBack onClick={() => {
            history.goBack();
          }}
          >
            <IconBase className="icon-arrow-left" />
            <span>Back</span>
          </ButtonBack>
          <Header4>{data?.name}</Header4>
        </Meta>
        <Actions>
          {ACTIONS.map(renderAction)}
        </Actions>
      </Header>
      {children}
      {data?.items.length === 0 ? <NoContent title="Empty set" /> : null}
      <AddButton isShifted={!!openedItemId} data={actions} />
      <DeleteItemsModal
        text="You are about to delete selected set, are you sure?"
        itemText={data?.name}
        title="Delete item?"
        submitText="Yes"
        cancelText="Cancel"
        isOpened={deleteModalOpened}
        onClose={handleDeleteSetModalClose}
        onSubmit={handleDeleteSubmit}
        overlayClassName="Overlay"
      />
      <EditSetModal
        title="Edit set"
        iconClassName="icon-pencil"
        submitText="Edit set"
        cancelText="Cancel"
        isOpened={editModalOpened}
        onClose={handleEditSetModalClose}
        onSubmit={handleEditSubmit}
        overlayClassName="Overlay"
        placeholder="Come up with a name for your set"
        validation={validation}
        defaultValues={(data && !isLoading) ? {
          color: data.color,
          name: data.name,
        } : undefined}
      />
      <AddToContainerEntityModal
        isProjectList
        isMultipleAllowed={false}
        text=""
        name="id"
        title="Add set to project"
        placeholder="Search for projects"
        submitText="Save changes"
        cancelText="Cancel"
        isOpened={addToProjectModalOpened}
        isLoading={isProjectListLoading}
        onClose={handleAddToProjectModalClose}
        onSubmit={handleAddToProjectSubmit}
        onSearch={onProjectQueryChange}
        options={projectOptions}
        validation={projectIdValidation}
        defaultValue={undefined}
        overlayClassName="Overlay"
        cancelWidth="94px"
      />
      <PickItemsFromCatalogue
        width="1020px"
        title="Browse existing items"
        submitText="Submit"
        isOpened={isCatalogueOpened}
        onClose={handleCatalogueModalClose}
        onSubmit={handleCatalogueSubmit}
      />
      <AddItemModal
        isOpened={isAddItemModalOpened}
        onClose={() => toggleAddItemModalOpened(false)}
        defaultData={id ? { sets: [id] } : {}}
        disabledFields={['sets']}
        overlayClassName="Overlay"
        placeholder="Name this item (required)"
      />
    </Container>
  );
};

export default Set;
